import { isInBounds, parseKeyX, parseKeyY, parseKeyZ, Position, posKeyXYZ, dominantAxis, axisSign, mortonStepAxis, forEachPlaneNeighborKey, axisCoordFromKey } from '../voxelMath'

import type { VoxelMap } from '../voxelTypes'
import { Vector3 } from '@/math'
import { faceToNormal, type Face } from '../voxelLiterals'

/**
 * Find all voxels that form a contiguous face along a given normal.
 * Uses flood fill to find connected voxels on the same plane.
 */
export function findContiguousFace(
  startPos: Position,
  normal: Vector3,
  grid: VoxelMap,
  sameTypeAndColor: boolean = true
): Set<number> {
  const result = new Set<number>()
  const startKey = posKeyXYZ(startPos.x, startPos.y, startPos.z)
  const startVoxel = grid.get(startKey)
  const targetType = startVoxel?.type
  const targetColor = startVoxel?.color

  const axis = dominantAxis(normal)
  const sign = axisSign(normal, axis)

  const stack: number[] = [startKey]
  const visited = new Set<number>()

  while (stack.length > 0) {
    const key = stack.pop()!
    if (visited.has(key)) continue
    visited.add(key)

    const voxel = grid.get(key)
    if (!voxel) continue
    if (sameTypeAndColor && (voxel.type !== targetType || voxel.color !== targetColor)) continue

    // Exposed if adjacent along +normal dir is empty (or out-of-bounds, which also yields empty)
    const frontKey = mortonStepAxis(key, axis, sign)
    if (!grid.has(frontKey)) result.add(key)

    // Explore 4-neighborhood on the same plane
    forEachPlaneNeighborKey(key, axis, (nk) => {
      if (!visited.has(nk) && grid.has(nk)) stack.push(nk)
    })
  }

  return result
}

/**
 * Find contiguous face by type only (ignore color differences).
 */
export function findContiguousFaceByType(
  startPos: Position,
  normal: Vector3,
  grid: VoxelMap
): Set<number> {
  const result = new Set<number>()
  const startKey = posKeyXYZ(startPos.x, startPos.y, startPos.z)
  const startVoxel = grid.get(startKey)
  const targetType = startVoxel?.type

  const axis = dominantAxis(normal)
  const sign = axisSign(normal, axis)

  const stack: number[] = [startKey]
  const visited = new Set<number>()

  while (stack.length > 0) {
    const key = stack.pop()!
    if (visited.has(key)) continue
    visited.add(key)

    const voxel = grid.get(key)
    if (!voxel) continue
    if (targetType != null && voxel.type !== targetType) continue

    const frontKey = mortonStepAxis(key, axis, sign)
    if (!grid.has(frontKey)) result.add(key)

    forEachPlaneNeighborKey(key, axis, (nk) => {
      if (!visited.has(nk) && grid.has(nk)) stack.push(nk)
    })
  }

  return result
}

/**
 * Create a voxel map for the face plate preview
 */
export function createFacePlate(
  faceKeys: Set<number>,
  normal: Vector3,
  offset: number,
  bounds: Position,
  voxelData: { color: number; type: string }
): VoxelMap {
  const plate = new Map() as VoxelMap

  for (const key of faceKeys) {
    const x = parseKeyX(key)
    const y = parseKeyY(key)
    const z = parseKeyZ(key)
    const newPos = {
      x: x + Math.round(normal.x * offset),
      y: y + Math.round(normal.y * offset),
      z: z + Math.round(normal.z * offset)
    }

    if (isInBounds(newPos, bounds)) {
      const newKey = posKeyXYZ(newPos.x, newPos.y, newPos.z)
      plate.set(newKey, voxelData)
    }
  }

  return plate
}

/**
 * Compute the set of keys that comprise the outward-facing boundary of a mask
 * in the given face direction. This collects boundary voxels across all
 * disconnected islands in the mask.
 */
export function findDirectionalFaceInMask(
  mask: VoxelMap,
  face: Face,
  bounds: Position
): Set<number> {
  const n = faceToNormal(face)
  const axis: 'x' | 'y' | 'z' = (Math.abs(n.x) > 0 ? 'x' : (Math.abs(n.y) > 0 ? 'y' : 'z'))
  const dir: 1 | -1 = ((axis === 'x' ? n.x : axis === 'y' ? n.y : n.z) >= 0 ? 1 : -1)
  const out = new Set<number>()
  for (const key of mask.keys()) {
    const c = axisCoordFromKey(key, axis)
    const max = axis === 'x' ? bounds.x : axis === 'y' ? bounds.y : bounds.z
    if ((dir > 0 && c >= max - 1) || (dir < 0 && c <= 0)) { out.add(key); continue }
    const nKey = mortonStepAxis(key, axis, dir)
    if (!mask.has(nKey)) out.add(key)
  }
  return out
}
